/**
 * <LiveMoreSheet> — le menu ⋮ de la barre du live (privé à `<LiveControlBar>`).
 *
 * Une feuille montée du bas, comme `<ActionSheet>` dont elle reprend la surface : « Un problème ? »
 * quand la barre n'a pas la place de le montrer, puis l'apparence (Automatique · Clair · Sombre),
 * qui s'applique sans quitter le live. L'apparence est la même préférence que celle de « Mon compte »
 * (`<AppearanceSection>`, reprise telle quelle) : la feuille ne sait ni où elle est gardée ni ce
 * qu'elle change. Choisir ne referme pas la feuille : l'élève voit le live changer derrière elle, et
 * la referme au voile.
 */
import React, { useContext } from "react"
import { Dimensions, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { LabeledIconButton } from "../../atoms/Buttons/LabeledIconButton"
import { Overlay, SHEET_EDGE_WIDTH } from "../../atoms/Overlay"
import { shadowStyle } from "../../utils/shadow"
import { AppearanceSection, type AppearanceChoice } from "../ProfileSwitcherSheet/AppearanceSection"

export type { AppearanceChoice }

/** L'apparence du live : la valeur gardée, et le geste qui la change. */
export type UILiveAppearance = {
    value: AppearanceChoice
    onChange: (value: AppearanceChoice) => void
}

export type LiveMoreSheetProps = {
    visible: boolean
    onClose: () => void
    /** « Un problème ? » dans le menu, quand la barre ne le montre pas. Absent : pas de ligne. */
    onOpenHelp?: () => void
    helpLabel: string
    appearance?: UILiveAppearance
    testID?: string
}

/** Au-delà, la feuille garde la largeur de `<ActionSheet>` et de `<Modal>`. */
const LARGE_FROM = 800
const LARGE_WIDTH = 500

export function LiveMoreSheet(props: LiveMoreSheetProps) {
    const { visible, onClose, onOpenHelp, helpLabel, appearance, testID } = props
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const insets = useSafeAreaInsets()

    return (
        <Overlay visible={visible} onRequestClose={onClose} placement="bottom" testID={testID}>
            <View style={styles.sheet(insets.bottom, dimensions.width > LARGE_FROM)}>
                <View style={styles.handle} />
                {onOpenHelp != null && (
                    <LabeledIconButton
                        variant="ghost"
                        size="md"
                        fullWidth
                        iconName="stethoscope"
                        label={helpLabel}
                        onPress={() => {
                            onClose()
                            onOpenHelp()
                        }}
                    />
                )}
                {appearance != null && (
                    <AppearanceSection
                        value={appearance.value}
                        onChange={appearance.onChange}
                        testID={testID != null ? `${testID}-appearance` : undefined}
                    />
                )}
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    sheet: (bottomInset: number, isLarge: boolean) => ({
        width: isLarge ? LARGE_WIDTH : ("100%" as const),
        backgroundColor: theme.colors["surface-raised"],
        borderTopLeftRadius: theme.radius.lg,
        borderTopRightRadius: theme.radius.lg,
        // Le liseré du bord haut, comme toute feuille qui monte du bas : visible sur la nuit (`sheet-edge`).
        borderTopWidth: SHEET_EDGE_WIDTH,
        borderColor: theme.colors["sheet-edge"],
        paddingTop: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
        paddingBottom: Math.max(bottomInset, theme.spacing["5"]),
        ...shadowStyle(theme, "sheet"),
    }),
    handle: {
        width: 40,
        height: 4,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.border,
        alignSelf: "center" as const,
        marginBottom: theme.spacing["3"],
    },
}))
